import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';
import { FiCalendar, FiClock, FiX } from 'react-icons/fi';
import CalendarDropdown from './CalendarDropdown';
import './CustomDateTimePicker.css';

const ITEM_HEIGHT = 31;

const CustomDateTimePicker = ({
    value,
    onChange,
    min,
    name,
    className = '',
    disabled = false,
    step,
    placeholder,
    required = false,
    id,
}) => {
    const { t, i18n } = useTranslation('common');
    const resolvedPlaceholder = placeholder || t('timePicker.selectDateTime');

    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('date');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const hourColRef = useRef(null);
    const minuteColRef = useRef(null);

    const minuteStep = step ? Math.floor(step / 60) : 15;

    const datePart = value ? value.split('T')[0] : '';
    const timePart = value ? value.split('T')[1] || '' : '';
    const currentHour = timePart ? parseInt(timePart.split(':')[0], 10) : null;
    const currentMinute = timePart ? parseInt(timePart.split(':')[1], 10) : null;

    const minDate = min ? min.split('T')[0] : undefined;
    const minTime = useMemo(() => {
        if (!min || !datePart) return undefined;
        const minDateStr = min.split('T')[0];
        if (datePart === minDateStr) {
            return min.split('T')[1] || undefined;
        }
        return undefined;
    }, [min, datePart]);

    const hours = useMemo(() => {
        const arr = [];
        for (let h = 0; h < 24; h++) arr.push(h);
        return arr;
    }, []);

    const minutes = useMemo(() => {
        const arr = [];
        const s = Math.max(1, minuteStep);
        for (let m = 0; m < 60; m += s) arr.push(m);
        return arr;
    }, [minuteStep]);

    const isHourDisabled = useCallback((hour) => {
        if (!minTime) return false;
        const minH = parseInt(minTime.split(':')[0], 10);
        return hour < minH;
    }, [minTime]);

    const isMinuteDisabled = useCallback((minute) => {
        if (!minTime) return false;
        const h = currentHour !== null ? currentHour : 0;
        const timeStr = `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        return timeStr < minTime;
    }, [minTime, currentHour]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 480);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const scrollToSelected = useCallback((ref, index) => {
        if (ref.current && index >= 0) {
            const scrollTop = index * ITEM_HEIGHT - (ref.current.clientHeight / 2 - ITEM_HEIGHT / 2);
            ref.current.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
        }
    }, []);

    // Scroll to selected hour/minute when dropdown opens
    useEffect(() => {
        if (!isOpen) return;
        if (isMobile && activeTab !== 'time') return;

        const timer = setTimeout(() => {
            if (currentHour !== null) {
                scrollToSelected(hourColRef, hours.indexOf(currentHour));
            }
            if (currentMinute !== null) {
                scrollToSelected(minuteColRef, minutes.indexOf(currentMinute));
            }
        }, 150);

        return () => clearTimeout(timer);
    }, [isOpen, activeTab, isMobile, currentHour, currentMinute, hours, minutes, scrollToSelected]);

    const positionDropdown = useCallback(() => {
        if (!inputRef.current || isMobile) return;

        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 360;
        const dropdownWidth = 380;

        const openBelow = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

        let left = rect.left;
        if (left + dropdownWidth > window.innerWidth - 8) {
            left = window.innerWidth - dropdownWidth - 8;
        }
        if (left < 8) left = 8;

        setDropdownStyle({
            position: 'fixed',
            left: `${left}px`,
            width: `${dropdownWidth}px`,
            ...(openBelow
                ? { top: `${rect.bottom + 4}px` }
                : { bottom: `${window.innerHeight - rect.top + 4}px` }),
            zIndex: 99999,
        });
    }, [isMobile]);

    const handleOpen = useCallback(() => {
        if (disabled) return;
        setActiveTab('date');
        positionDropdown();
        setIsOpen(true);
    }, [disabled, positionDropdown]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const emitChange = useCallback((date, time) => {
        if (!date) return;
        const t = time || '00:00';
        const newValue = `${date}T${t}`;
        const syntheticEvent = {
            target: { name: name || '', value: newValue },
            currentTarget: { name: name || '', value: newValue },
        };
        onChange(syntheticEvent);
    }, [name, onChange]);

    const handleDateSelect = useCallback((dateStr) => {
        emitChange(dateStr, timePart || '00:00');
        if (isMobile) {
            setActiveTab('time');
        }
    }, [emitChange, timePart, isMobile]);

    const handleHourClick = useCallback((hour) => {
        if (isHourDisabled(hour)) return;
        const min = currentMinute !== null ? currentMinute : minutes[0];
        const d = datePart || dayjs().format('YYYY-MM-DD');
        const newTime = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
        emitChange(d, newTime);
    }, [currentMinute, minutes, datePart, emitChange, isHourDisabled]);

    const handleMinuteClick = useCallback((minute) => {
        if (isMinuteDisabled(minute)) return;
        const hr = currentHour !== null ? currentHour : 0;
        const d = datePart || dayjs().format('YYYY-MM-DD');
        const newTime = `${String(hr).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        emitChange(d, newTime);
    }, [currentHour, datePart, emitChange, isMinuteDisabled]);

    const handleClear = useCallback((e) => {
        e.stopPropagation();
        const syntheticEvent = {
            target: { name: name || '', value: '' },
            currentTarget: { name: name || '', value: '' },
        };
        onChange(syntheticEvent);
    }, [name, onChange]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e) => {
            if (
                inputRef.current && !inputRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                handleClose();
            }
        };

        const handleEscape = (e) => {
            if (e.key === 'Escape') handleClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, handleClose]);

    useEffect(() => {
        if (isOpen && !isMobile) {
            positionDropdown();
            window.addEventListener('scroll', positionDropdown, true);
            window.addEventListener('resize', positionDropdown);
            return () => {
                window.removeEventListener('scroll', positionDropdown, true);
                window.removeEventListener('resize', positionDropdown);
            };
        }
    }, [isOpen, isMobile, positionDropdown]);

    const displayValue = value
        ? `${dayjs(datePart).locale(i18n.language).format('D MMMM YYYY')}, ${timePart}`
        : '';

    const renderTimeColumns = () => (
        <div className="cdtp-time-section">
            <div className="cdtp-time-labels">
                <span className="cdtp-time-label">{t('timePicker.hours')}</span>
                <span className="cdtp-time-label">{t('timePicker.minutes')}</span>
            </div>
            <div className="cdtp-time-columns">
                <div className="cdtp-time-col" ref={hourColRef}>
                    {hours.map(h => {
                        const isDisabled = isHourDisabled(h);
                        const isSelected = h === currentHour;
                        return (
                            <button
                                key={h}
                                type="button"
                                className={`cdtp-time-item ${isSelected ? 'cdtp-time-item--selected' : ''} ${isDisabled ? 'cdtp-time-item--disabled' : ''}`}
                                onClick={() => handleHourClick(h)}
                                disabled={isDisabled}
                            >
                                {String(h).padStart(2, '0')}
                            </button>
                        );
                    })}
                </div>
                <div className="cdtp-time-divider" />
                <div className="cdtp-time-col" ref={minuteColRef}>
                    {minutes.map(m => {
                        const isDisabled = isMinuteDisabled(m);
                        const isSelected = m === currentMinute;
                        return (
                            <button
                                key={m}
                                type="button"
                                className={`cdtp-time-item ${isSelected ? 'cdtp-time-item--selected' : ''} ${isDisabled ? 'cdtp-time-item--disabled' : ''}`}
                                onClick={() => handleMinuteClick(m)}
                                disabled={isDisabled}
                            >
                                {String(m).padStart(2, '0')}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const renderDropdown = () => {
        if (!isOpen) return null;

        if (isMobile) {
            return createPortal(
                <div className="cdtp-picker-overlay" onClick={handleClose}>
                    <div
                        className="cdtp-picker-sheet"
                        ref={dropdownRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="cdtp-picker-sheet-handle" />
                        <div className="cdtp-content cdtp-content--mobile">
                            <div className="cdtp-tabs">
                                <button
                                    type="button"
                                    className={`cdtp-tab ${activeTab === 'date' ? 'cdtp-tab--active' : ''}`}
                                    onClick={() => setActiveTab('date')}
                                >
                                    <FiCalendar className="cdtp-tab-icon" />
                                    <span className="cdtp-tab-label">{t('timePicker.date')}</span>
                                    {datePart && (
                                        <span className="cdtp-tab-value">{dayjs(datePart).format('D MMM')}</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`cdtp-tab ${activeTab === 'time' ? 'cdtp-tab--active' : ''}`}
                                    onClick={() => setActiveTab('time')}
                                >
                                    <FiClock className="cdtp-tab-icon" />
                                    <span className="cdtp-tab-label">{t('timePicker.time')}</span>
                                    {timePart && (
                                        <span className="cdtp-tab-value">{timePart}</span>
                                    )}
                                </button>
                            </div>
                            <div className="cdtp-tab-body">
                                {activeTab === 'date' ? (
                                    <CalendarDropdown
                                        selectedDate={datePart}
                                        onChange={handleDateSelect}
                                        minDate={minDate}
                                        onClose={handleClose}
                                    />
                                ) : (
                                    renderTimeColumns()
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            );
        }

        return createPortal(
            <div ref={dropdownRef} className="cdtp-picker-dropdown" style={dropdownStyle}>
                <div className="cdtp-content cdtp-content--desktop">
                    <div className="cdtp-calendar-section">
                        <CalendarDropdown
                            selectedDate={datePart}
                            onChange={handleDateSelect}
                            minDate={minDate}
                            onClose={handleClose}
                        />
                    </div>
                    {renderTimeColumns()}
                </div>
            </div>,
            document.body
        );
    };

    return (
        <>
            <div
                ref={inputRef}
                className={`cdtp-picker-input ${isOpen ? 'cdtp-picker-input--open' : ''} ${disabled ? 'cdtp-picker-input--disabled' : ''} ${className}`}
                onClick={handleOpen}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); } }}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                id={id}
            >
                <FiCalendar className="cdtp-picker-icon" />
                <span className={`cdtp-picker-text ${!value ? 'cdtp-picker-text--placeholder' : ''}`}>
                    {displayValue || resolvedPlaceholder}
                </span>
                {value && !required && !disabled && (
                    <button
                        type="button"
                        className="cdtp-picker-clear"
                        onClick={handleClear}
                        aria-label={t('timePicker.clear')}
                        tabIndex={-1}
                    >
                        <FiX />
                    </button>
                )}
            </div>
            {renderDropdown()}
        </>
    );
};

export default CustomDateTimePicker;
